import axios from 'axios';

import { API_URL, API_TRANSLATIONS, TRANSLATIONS_VERSION } from 'src/constants';

import { MESSAGES_VERSION_KEY } from 'utils/messages/constants';
import { action, request } from 'utils/mobx';
import { translationsUrl } from 'utils/translations.url';

const loadAction = 'LOAD_TRANSLATIONS';
const checkAction = 'CHECK_TRANSLATIONS_ETAG';

function generateTranslationsService() {
  class Translations {
    @request({ action: checkAction })
    headRequest(locale: availableLocales): any {
      return axios.head(translationsUrl() + locale + '.json');
    }

    @request({ action: loadAction })
    fetchRequest(locale: availableLocales): any {
      return axios.get(translationsUrl() + locale + '.json', {
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
    }

    @action({ action: loadAction })
    async fetch(locale: availableLocales): Promise<[number, null | ApiTranslations]> {
      // Check if language changed - if so, always fetch fresh data
      const currentLocale = localStorage.getItem(`${MESSAGES_VERSION_KEY}_current_locale`);
      const languageChanged = currentLocale !== locale;

      if (languageChanged) {
        // Language changed - always fetch fresh data
        const [fetchStatus, fetchResponse] = await this.fetchRequest(locale);
        
        if (!fetchStatus) {
          const cachedVersion = Number(localStorage.getItem(MESSAGES_VERSION_KEY)) ?? 0;
          return [cachedVersion, null];
        }

        // Store new locale and version from last-modified
        const serverLastModified = fetchResponse.headers['last-modified'];
        const newVersion = serverLastModified ? new Date(serverLastModified).getTime() : Date.now();
        
        localStorage.setItem(MESSAGES_VERSION_KEY, newVersion.toString());
        localStorage.setItem(`${MESSAGES_VERSION_KEY}_current_locale`, locale);

        return [newVersion, fetchResponse.data];
      }

      // Same language - check if server has updates using HEAD request
      const [headStatus, headResponse] = await this.headRequest(locale);
      
      if (!headStatus) {
        const cachedVersion = Number(localStorage.getItem(MESSAGES_VERSION_KEY)) ?? 0;
        return [cachedVersion, null];
      }

      const serverLastModified = headResponse.headers['last-modified'];
      const localVersion = Number(localStorage.getItem(MESSAGES_VERSION_KEY)) ?? 0;
      const serverVersion = serverLastModified ? new Date(serverLastModified).getTime() : Date.now();

      // Compare last-modified timestamps
      if (serverVersion <= localVersion) {
        // Server version is same or older - return cached data indicator
        return [localVersion, null];
      }

      // Server has newer version - fetch fresh translations
      const [fetchStatus, fetchResponse] = await this.fetchRequest(locale);
      
      if (!fetchStatus) {
        return [localVersion, null];
      }

      // Store new version from last-modified
      const newServerLastModified = fetchResponse.headers['last-modified'];
      const newVersion = newServerLastModified ? new Date(newServerLastModified).getTime() : Date.now();
      
      localStorage.setItem(MESSAGES_VERSION_KEY, newVersion.toString());
      localStorage.setItem(`${MESSAGES_VERSION_KEY}_current_locale`, locale);

      return [newVersion, fetchResponse.data];
    }

    clearCache(): void {
      // Clear translation cache (single key)
      localStorage.removeItem(MESSAGES_VERSION_KEY);
      localStorage.removeItem(`${MESSAGES_VERSION_KEY}_current_locale`);
    }
  }

  return new Translations();
}

export { generateTranslationsService };
